import React, { useRef, useMemo, useContext } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { ThemeContext } from '../../theme/ThemeContext';
import * as THREE from 'three';
import './NeuralNetworkVisual.css';

/**
 * Neural Network Node Component
 * Glowing circular nodes representing neurons with enhanced animations
 */
function NeuralNode({ position, size = 0.08, delay = 0, nodeColor = '#059669' }) {
  const meshRef = useRef();
  const glowRef = useRef();
  const materialRef = useRef();
  const groupRef = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    // Floating motion
    if (groupRef.current) {
      groupRef.current.position.y = position[1] + Math.sin(time * 0.8 + delay) * 0.15;
      groupRef.current.position.x = position[0] + Math.cos(time * 0.6 + delay) * 0.08;
    }
    
    // Enhanced pulsing scale animation
    if (meshRef.current) {
      const pulse = Math.sin(time * 2.5 + delay) * 0.4 + 1.0;
      meshRef.current.scale.setScalar(pulse);
    }
    
    // Pulsing emissive intensity
    if (materialRef.current) {
      const intensity = Math.sin(time * 2 + delay) * 0.3 + 0.9;
      materialRef.current.emissiveIntensity = intensity;
    }
    
    // Light intensity animation
    if (glowRef.current) {
      const intensity = Math.sin(time * 1.8 + delay) * 0.5 + 0.8;
      glowRef.current.intensity = intensity;
    }
  });

  return (
    <group ref={groupRef} position={position}>
      {/* Outer glow light */}
      <pointLight
        ref={glowRef}
        color={nodeColor}
        intensity={0.8}
        distance={2}
        decay={2}
      />
      {/* Node sphere */}
      <mesh ref={meshRef} castShadow>
        <sphereGeometry args={[size, 16, 16]} />
        <meshStandardMaterial
          ref={materialRef}
          color={nodeColor}
          emissive={nodeColor}
          emissiveIntensity={0.9}
        />
      </mesh>
    </group>
  );
}

/**
 * Connection Line Component
 * Animated lines connecting neurons with flowing data effect
 */
function ConnectionLine({ start, end, delay = 0, lineColor = '#0D9488', index = 0 }) {
  const materialRef = useRef();
  const lineRef = useRef();

  const positions = useMemo(
    () => new Float32Array([...start, ...end]),
    [start, end]
  );

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    if (materialRef.current) {
      // Sequential flow effect - lines pulse in waves
      const wave = Math.sin(time * 1.5 + delay + index * 0.2) * 0.4 + 0.6;
      materialRef.current.opacity = wave;
      
      // Color intensity pulse
      const colorIntensity = Math.sin(time * 1.8 + delay) * 0.2 + 0.8;
      materialRef.current.color.setStyle(lineColor).multiplyScalar(colorIntensity);
    }
  });

  return (
    <line ref={lineRef}>
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          count={2}
          array={positions}
          itemSize={3}
        />
      </bufferGeometry>
      <lineBasicMaterial
        ref={materialRef}
        color={lineColor}
        transparent
        opacity={0.6}
      />
    </line>
  );
}

/**
 * Central Glow Component
 * Bright central glow representing AI core/activity with enhanced animations
 */
function CentralGlow({ position, coreColor = '#14B8A6' }) {
  const glowRef = useRef();
  const ringRef = useRef();
  const glowMaterialRef = useRef();
  const ringMaterialRef = useRef();
  const groupRef = useRef();
  const coreLightRef = useRef();

  useFrame((state) => {
    const time = state.clock.elapsedTime;
    
    // Floating motion for entire core
    if (groupRef.current) {
      groupRef.current.position.y = position[1] + Math.sin(time * 0.6) * 0.2;
    }
    
    // Enhanced breathing glow effect
    if (glowRef.current) {
      const scale = 1 + Math.sin(time * 0.9) * 0.25;
      glowRef.current.scale.setScalar(scale);
    }
    
    // Pulsing emissive intensity
    if (glowMaterialRef.current) {
      const intensity = Math.sin(time * 1.4) * 0.4 + 0.8;
      glowMaterialRef.current.emissiveIntensity = intensity;
      glowMaterialRef.current.opacity = 0.3 + Math.sin(time * 1.2) * 0.2;
    }
    
    // Rotating ring with pulsing
    if (ringRef.current) {
      ringRef.current.rotation.z = time * 0.5;
      const ringScale = 1 + Math.sin(time * 1.1) * 0.1;
      ringRef.current.scale.setScalar(ringScale);
    }
    
    if (ringMaterialRef.current) {
      const intensity = Math.sin(time * 1.6) * 0.3 + 0.9;
      ringMaterialRef.current.emissiveIntensity = intensity;
    }
    
    // Core light pulsing
    if (coreLightRef.current) {
      const intensity = Math.sin(time * 1.3) * 0.4 + 1.2;
      coreLightRef.current.intensity = intensity;
    }
  });

  return (
    <group ref={groupRef} position={position}>
      {/* Core light */}
      <pointLight
        ref={coreLightRef}
        color={coreColor}
        intensity={1.2}
        distance={4}
        decay={1.5}
      />
      
      {/* Central bright glow sphere */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[0.5, 32, 32]} />
        <meshStandardMaterial
          ref={glowMaterialRef}
          color={coreColor}
          emissive={coreColor}
          emissiveIntensity={0.8}
          transparent
          opacity={0.4}
        />
      </mesh>
      
      {/* Rotating ring */}
      <mesh ref={ringRef} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[0.6, 0.025, 16, 32]} />
        <meshStandardMaterial
          ref={ringMaterialRef}
          color={coreColor}
          emissive={coreColor}
          emissiveIntensity={0.9}
        />
      </mesh>
    </group>
  );
}

/**
 * Scene Content Component (rendered inside Canvas)
 * Contains all 3D elements with floating animation
 */
function SceneContent({ nodeColor, lineColor, coreColor, isDark, nodes, connections }) {
  const sceneGroupRef = useRef();

  // Overall scene floating and rotation (this useFrame is inside Canvas, so it's valid)
  useFrame((state) => {
    if (sceneGroupRef.current) {
      const time = state.clock.elapsedTime;
      // Subtle floating motion
      sceneGroupRef.current.position.y = Math.sin(time * 0.4) * 0.1;
      // Gentle rotation
      sceneGroupRef.current.rotation.y = Math.sin(time * 0.3) * 0.05;
    }
  });

  return (
    <group ref={sceneGroupRef}>
      {/* Lighting */}
      <ambientLight intensity={isDark ? 0.3 : 0.6} />
      <pointLight position={[0, 0, 0]} intensity={isDark ? 1.5 : 0.8} color={coreColor} />
      <pointLight position={[-3, 2, 3]} intensity={0.4} color={nodeColor} />
      <pointLight position={[3, -2, -3]} intensity={0.4} color={nodeColor} />

      {/* Central glow */}
      <CentralGlow position={[-0.5, 0.2, 0]} coreColor={coreColor} />

      {/* Connection lines */}
      {connections.map(([startIdx, endIdx, dist], i) => (
        <ConnectionLine
          key={`conn-${startIdx}-${endIdx}`}
          start={nodes[startIdx]}
          end={nodes[endIdx]}
          delay={i * 0.1}
          lineColor={lineColor}
          index={i}
        />
      ))}

      {/* Neural nodes */}
      {nodes.map((pos, i) => (
        <NeuralNode
          key={`node-${i}`}
          position={pos}
          size={0.06 + (i % 3) * 0.02}
          delay={i * 0.15}
          nodeColor={nodeColor}
        />
      ))}
    </group>
  );
}

/**
 * Neural Network Scene
 * Main component with nodes, connections, and central glow
 */
function NeuralNetworkScene({ nodeColor, lineColor, coreColor }) {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  // Node positions - arranged in a network pattern (similar to dark mode reference)
  const nodes = useMemo(() => [
    // Left cluster
    [-2, 1.2, 0],
    [-1.8, 0.5, 0.3],
    [-2.2, -0.2, -0.2],
    [-1.5, -0.8, 0.2],
    // Center-left (dense area - main network)
    [-0.8, 1.5, 0],
    [-0.6, 0.8, 0.4],
    [-1, 0.2, -0.3],
    [-0.5, -0.5, 0.3],
    [-0.9, -1.2, -0.2],
    [-1.2, 0.6, -0.4],
    // Center (core area)
    [0, 0.5, 0],
    [0.3, 1, 0.2],
    [-0.2, -0.3, 0.3],
    // Right cluster
    [1.2, 0.8, -0.2],
    [1.5, 0, 0.2],
    [1, -0.8, 0],
    [1.8, -0.5, -0.3],
  ], []);

  // Connection pairs - connect nodes to form network
  const connections = useMemo(() => {
    const conns = [];
    // Connect nodes in clusters and between clusters
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dist = Math.sqrt(
          Math.pow(nodes[i][0] - nodes[j][0], 2) +
          Math.pow(nodes[i][1] - nodes[j][1], 2) +
          Math.pow(nodes[i][2] - nodes[j][2], 2)
        );
        // Connect nearby nodes (within 2.5 units) and some longer connections
        if (dist < 2.5 || (dist < 3.5 && Math.random() > 0.7)) {
          conns.push([i, j, dist]);
        }
      }
    }
    return conns.sort((a, b) => a[2] - b[2]).slice(0, 28); // Limit connections for performance
  }, [nodes]);

  return (
    <div className="neural-network-visual">
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        gl={{ antialias: true, alpha: true }}
      >
        {/* Scene content with all 3D elements - this component is inside Canvas */}
        <SceneContent
          nodeColor={nodeColor}
          lineColor={lineColor}
          coreColor={coreColor}
          isDark={isDark}
          nodes={nodes}
          connections={connections}
        />
      </Canvas>
    </div>
  );
}

/**
 * Main Neural Network Visual Component
 * Wraps the 3D scene with theme-aware styling
 */
function NeuralNetworkVisual() {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === 'dark';

  // Theme-aware colors
  const nodeColor = isDark ? '#42F2A9' : '#059669';
  const lineColor = isDark ? 'rgba(66, 242, 169, 0.7)' : '#0D9488';
  const coreColor = isDark ? '#42F2A9' : '#14B8A6';

  return (
    <div className={`neural-network-wrapper ${isDark ? 'neural-network-wrapper--dark' : 'neural-network-wrapper--light'}`}>
      <div className="neural-network-content">
        <h1 className="neural-network-title">Where clarity begins</h1>
        <p className="neural-network-tagline">Better work follows</p>
        <div className="neural-network-scene-container">
          <NeuralNetworkScene 
            nodeColor={nodeColor}
            lineColor={lineColor}
            coreColor={coreColor}
          />
        </div>
      </div>
    </div>
  );
}

export default NeuralNetworkVisual;
